import { useEffect, useRef, useState, type ReactElement, type ReactNode } from 'react';
import {
  Autocomplete,
  Button,
  Dialog,
  Header,
  Input,
  Menu,
  MenuItem,
  MenuSection,
  Modal,
  ModalOverlay,
  SearchField,
} from 'react-aria-components';
import { EmptyState } from '../ui/EmptyState';
import { Icon, type IconName } from '../ui/Icon';
import type { RecentFn, SearchFn, SearchResult, SearchResultKind, SearchResults } from './types';
import s from './SearchPalette.module.css';

export interface SearchPaletteProps {
  isOpen: boolean;
  onOpenChange: (isOpen: boolean) => void;
  /** Server search (APP-022): only returns what the person may already see. */
  search: SearchFn;
  /** Before anything is typed: shortcuts for the person's roles (SHELL1). */
  jumpTo?: readonly SearchResult[];
  /**
   * Before anything is typed: what the person opened lately, fetched (and re-checked) each time
   * the palette opens. Shown above Jump to once it arrives; nothing if it can't be fetched.
   */
  recent?: RecentFn;
  /** Delay before searching while typing, in ms. */
  debounceMs?: number;
}

const GROUPS = [
  { key: 'pages', label: 'Pages' },
  { key: 'people', label: 'People' },
  { key: 'awards', label: 'Awards' },
] as const satisfies ReadonlyArray<{ key: keyof SearchResults; label: string }>;

interface Group {
  key: string;
  label: string;
  items: readonly SearchResult[];
}

const NONE: readonly SearchResult[] = [];

const KIND: Record<SearchResultKind, { icon: IconName; label: string }> = {
  page: { icon: 'page', label: 'Page' },
  person: { icon: 'person', label: 'Person' },
  award: { icon: 'award', label: 'Award' },
};

const EMPTY: SearchResults = { pages: [], people: [], awards: [] };

type Status = 'idle' | 'loading' | 'done' | 'error';

function total(results: SearchResults): number {
  return results.pages.length + results.people.length + results.awards.length;
}

function resultCount(count: number): string {
  return count === 1 ? '1 result' : `${count} results`;
}

/** Wraps the first match of the query in <mark>. */
function highlight(label: string, query: string): ReactNode {
  const at = query === '' ? -1 : label.toLowerCase().indexOf(query.toLowerCase());
  if (at < 0) return label;
  return (
    <>
      {label.slice(0, at)}
      <mark>{label.slice(at, at + query.length)}</mark>
      {label.slice(at + query.length)}
    </>
  );
}

/**
 * Search & jump (Ctrl K / ⌘K): one box for pages, people and awards. Arrow keys move, Enter opens,
 * Escape closes. Before anything is typed it offers Recent and Jump to (SHELL1); then results
 * come from the async `search` prop and are shown in groups.
 */
export function SearchPalette({
  isOpen,
  onOpenChange,
  search,
  jumpTo = NONE,
  recent,
  debounceMs = 150,
}: SearchPaletteProps): ReactElement {
  return (
    <ModalOverlay isOpen={isOpen} onOpenChange={onOpenChange} isDismissable className={s.overlay}>
      <Modal className={s.modal}>
        <Dialog aria-label="Search or jump to" className={s.dialog}>
          {({ close }) => (
            <PaletteContent
              search={search}
              jumpTo={jumpTo}
              recent={recent}
              debounceMs={debounceMs}
              onClose={close}
            />
          )}
        </Dialog>
      </Modal>
    </ModalOverlay>
  );
}

/** Lives only while the palette is open, so its query, results and Recent start fresh each time. */
function PaletteContent({
  search,
  jumpTo,
  recent,
  debounceMs,
  onClose,
}: {
  search: SearchFn;
  jumpTo: readonly SearchResult[];
  recent: RecentFn | undefined;
  debounceMs: number;
  onClose: () => void;
}): ReactElement {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<SearchResults>(EMPTY);
  const [status, setStatus] = useState<Status>('idle');
  const [recentItems, setRecentItems] = useState<readonly SearchResult[]>(NONE);
  const trimmed = query.trim();
  const inputRef = useRef<HTMLInputElement>(null);

  // Recent is re-checked as the palette opens. Jump to shows at once; Recent joins above it when
  // it arrives, and simply stays away if it can't be fetched.
  useEffect(() => {
    if (recent === undefined) return;
    const controller = new AbortController();
    recent({ signal: controller.signal }).then(
      (items) => {
        if (!controller.signal.aborted) setRecentItems(items);
      },
      () => undefined,
    );
    return () => {
      controller.abort();
    };
  }, [recent]);

  // Typing works the moment the palette opens (Ctrl K, ⌘K or the Search button). This child
  // effect runs before the Dialog's own, which then sees focus inside and leaves it there.
  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const onInputChange = (value: string): void => {
    setQuery(value);
    setStatus(value.trim() === '' ? 'idle' : 'loading');
  };

  useEffect(() => {
    if (trimmed === '') return;
    const controller = new AbortController();
    const timer = window.setTimeout(() => {
      search(trimmed, { signal: controller.signal }).then(
        (found) => {
          if (controller.signal.aborted) return;
          setResults(found);
          setStatus('done');
        },
        () => {
          if (controller.signal.aborted) return;
          setStatus('error');
        },
      );
    }, debounceMs);
    return () => {
      window.clearTimeout(timer);
      controller.abort();
    };
  }, [trimmed, search, debounceMs]);

  const shown = status === 'done' ? results : EMPTY;
  const groups: readonly Group[] =
    trimmed === ''
      ? [
          { key: 'recent', label: 'Recent', items: recentItems },
          { key: 'jump', label: 'Jump to', items: jumpTo },
        ]
      : GROUPS.map((group) => ({ ...group, items: shown[group.key] }));
  const announcement =
    trimmed === ''
      ? ''
      : status === 'loading'
        ? 'Searching…'
        : status === 'error'
          ? 'Search isn’t available right now.'
          : resultCount(total(shown));

  return (
    <>
      <Autocomplete inputValue={query} onInputChange={onInputChange}>
        <div className={s.field}>
          <Icon name="search" size={20} />
          <SearchField aria-label="Search or jump to" className={s.searchField}>
            <Input
              ref={inputRef}
              className={s.input}
              placeholder="Search pages, people and awards"
            />
          </SearchField>
          <Button className={s.escape} onPress={onClose}>
            <span className={s.kbd}>Esc</span>
            <span className="visually-hidden">, close search</span>
          </Button>
        </div>
        <div className={s.results}>
          <span className="visually-hidden" role="status">
            {announcement}
          </span>
          {status === 'error' ? (
            <div className={s.message}>
              <EmptyState
                variant="first-day"
                art="search"
                size="sm"
                headline="Search isn’t available right now"
                body="Try again in a moment. You can still use the menu at the top of the page."
              />
            </div>
          ) : (
            <Menu
              aria-label="Results"
              className={s.menu}
              onAction={onClose}
              renderEmptyState={() =>
                trimmed === '' ? (
                  <p className={s.status}>Type a page, a person or an award ID.</p>
                ) : status === 'loading' ? (
                  <p className={s.status}>Searching…</p>
                ) : (
                  <div className={s.message}>
                    <EmptyState
                      variant="first-day"
                      art="search"
                      size="sm"
                      headline={`No matches for “${trimmed}”`}
                      body="Try a name, an award ID like SDB-2026-0151, or a page like Team recognition. Search only covers what you can already see."
                    />
                  </div>
                )
              }
            >
              {groups
                .filter((group) => group.items.length > 0)
                .map((group) => (
                  <MenuSection key={group.key} id={group.key} className={s.section}>
                    <Header className={s.groupHeader}>
                      <span>{group.label}</span>
                      {trimmed !== '' && <span>{resultCount(group.items.length)}</span>}
                    </Header>
                    {group.items.map((result: SearchResult) => (
                      <MenuItem
                        // A page can be both recent and a shortcut: each group's item is its own.
                        key={`${group.key}:${result.id}`}
                        id={`${group.key}:${result.id}`}
                        href={result.href}
                        textValue={result.label}
                        className={s.item}
                      >
                        <span className={s.itemIcon} aria-hidden="true">
                          <Icon name={KIND[result.kind].icon} size={16} />
                        </span>
                        <span className={s.itemText}>
                          <span className={s.itemLabel}>{highlight(result.label, trimmed)}</span>
                          {result.meta !== undefined && (
                            <span className={s.itemMeta}>{result.meta}</span>
                          )}
                        </span>
                        <span className={s.itemKind}>{KIND[result.kind].label}</span>
                      </MenuItem>
                    ))}
                  </MenuSection>
                ))}
            </Menu>
          )}
        </div>
      </Autocomplete>
      <div className={s.foot} aria-hidden="true">
        <span className={s.hints}>
          <span className={s.hint}>
            <span className={s.kbd}>↑</span>
            <span className={s.kbd}>↓</span> Move
          </span>
          <span className={s.hint}>
            <span className={s.kbd}>Enter</span> Open
          </span>
          <span className={s.hint}>
            <span className={s.kbd}>Esc</span> Close
          </span>
        </span>
        <span>
          Open from anywhere with <span className={s.kbd}>Ctrl</span>{' '}
          <span className={s.kbd}>K</span>
        </span>
      </div>
    </>
  );
}
